import { DialogComponent } from '@theme/dialog';

class ProductInfoTabs extends DialogComponent {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    const refs = this.refs.tabs;
    const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
    return list.filter((el) => el instanceof HTMLButtonElement);
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    const refs = this.refs.panels;
    const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
    return list.filter((el) => el instanceof HTMLElement);
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleTriggerClick, { signal });

    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#handleTabClick, { signal });
      tab.addEventListener('keydown', this.#handleTabKeyDown, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Delegates clicks on any `[data-open-tab]` trigger (the three buy-box rows) to open the
   * dialog on the matching tab.
   * @param {Event} event
   */
  #handleTriggerClick = (event) => {
    // `Element`, not `HTMLElement`: a click on the arrow targets its SVG nodes.
    const trigger = event.target instanceof Element ? event.target.closest('[data-open-tab]') : null;
    if (!(trigger instanceof HTMLElement)) return;

    const targetId = trigger.dataset.openTab;
    if (targetId) this.#selectTab(targetId, { focus: false });
    this.showDialog();
  };

  /** @param {Event} event */
  #handleTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    const targetId = tab.dataset.tabTarget;
    if (targetId) this.#selectTab(targetId, { focus: false });
  };

  /** @param {KeyboardEvent} event */
  #handleTabKeyDown = (event) => {
    const tabs = this.#tabs;
    if (tabs.length === 0) return;
    if (!(event.currentTarget instanceof HTMLButtonElement)) return;

    const currentIndex = tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    /** @type {number | null} */
    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    const targetId = nextTab?.dataset.tabTarget;
    if (targetId) this.#selectTab(targetId, { focus: true });
  };

  /**
   * @param {string} targetId
   * @param {Object} [options]
   * @param {boolean} [options.focus] - Move focus to the newly active tab (keyboard navigation).
   */
  #selectTab(targetId, { focus = false } = {}) {
    for (const tab of this.#tabs) {
      const isSelected = tab.dataset.tabTarget === targetId;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
      if (isSelected && focus) tab.focus();

      // Mobile shows this text as the header title instead of the (desktop-only) tablist — cf.
      // .docs/product-page-integration.md, notes phase 5.
      if (isSelected && this.refs.mobileTitle instanceof HTMLElement) {
        this.refs.mobileTitle.textContent = tab.textContent?.trim() ?? '';
      }
    }

    for (const panel of this.#panels) {
      panel.hidden = panel.id !== targetId;
    }

    const scrollArea = this.refs.scrollArea;
    if (scrollArea instanceof HTMLElement) scrollArea.scrollTo({ top: 0 });
  }

  openDrawer() {
    this.showDialog();
  }

  closeDrawer() {
    this.closeDialog();
  }
}

if (!customElements.get('product-info-tabs')) {
  customElements.define('product-info-tabs', ProductInfoTabs);
}
